import React, { useState, useEffect } from 'react';
import { Calendar, Clock, Plus, User, Phone, Mail, ChevronLeft, ChevronRight, X } from 'lucide-react';
import '../../styles/CalendarManagement.css';
import BookingListView from './BookingListView';

const CalendarManagement = ({ calendarData: propCalendarData, services: propServices, onDataChange }) => {
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [view, setView] = useState('day');
  const [calendarData, setCalendarData] = useState(null);
  const [services, setServices] = useState([]);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [showModal, setShowModal] = useState(null);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [forceRefresh, setForceRefresh] = useState(0);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [availableSlots, setAvailableSlots] = useState([]);

  const [viewType, setViewType] = useState('timeline'); // 'timeline' or 'list'
const [dateRange, setDateRange] = useState({
  start: new Date(),
  end: new Date()
});
const [selectedServiceFilter, setSelectedServiceFilter] = useState('');
const [statusFilter, setStatusFilter] = useState('all');

  // DIRECT API CALL FUNCTION - Original working version
 const fetchCalendarData = async (dateToFetch, viewToUse) => {
  console.log('🔄 DIRECT API CALL STARTING');
  console.log('Date object:', dateToFetch);
  console.log('View:', viewToUse);
  
  setLoading(true);
  
  const year = dateToFetch.getFullYear();
  const month = String(dateToFetch.getMonth() + 1).padStart(2, '0');
  const day = String(dateToFetch.getDate()).padStart(2, '0');
  const formattedDate = `${year}-${month}-${day}`;
  
  console.log('📅 Formatted date:', formattedDate);
  
  const url = `/api/booking-v2/vendor/calendar?date=${formattedDate}&view=${viewToUse}&nocache=${Date.now()}`;
  console.log('🌐 Calling URL:', url);
  
  try {
    const response = await fetch(url, {
      method: 'GET',
      headers: { 
        'Authorization': `Bearer ${localStorage.getItem('token')}`,
        'Cache-Control': 'no-cache, no-store, must-revalidate',
        'Pragma': 'no-cache'
      }
    });
    
    console.log('📡 Response received, status:', response.status);
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    const data = await response.json();
    console.log('📊 Data received:', {
      bookings: data.bookings?.length || 0,
      date: data.date,
      startDate: data.startDate,
      endDate: data.endDate,
      businessHours: data.businessHours?.length || 0,
      blockedSlots: data.blockedSlots?.length || 0,
      allBookings: data.bookings
    });
    
    setCalendarData(data);
  } catch (error) {
    console.error('❌ API Error:', error);
    setCalendarData({ 
      bookings: [], 
      blockedSlots: [], 
      businessHours: [], 
      services: [] 
    });
  } finally {
    setLoading(false);
  }
};

// UPDATED: Main useEffect - handles both selectedDate and dateRange changes
// UPDATED: Main useEffect - Only loads initial data, doesn't auto-fetch on dateRange change
useEffect(() => {
  if (propCalendarData && propServices) {
    setCalendarData(propCalendarData);
    setServices(propServices);
    setLoading(false);
  } else if (!calendarData) {
    // Only fetch on initial load
    fetchCalendarData(selectedDate, view);
  }
}, [propCalendarData, propServices]);

// Separate effect for manual view changes only
useEffect(() => {
  if (!propCalendarData && selectedDate && forceRefresh > 0) {
    console.log('👁️ View or force refresh changed');
    fetchCalendarData(selectedDate, view);
  }
}, [view, forceRefresh]);

// UPDATED: Separate effect for view changes (when user manually changes view)
useEffect(() => {
  // Only fetch if view was manually changed, not from date range calculation
  if (!propCalendarData && selectedDate) {
    console.log('👁️ View manually changed to:', view);
    fetchCalendarData(selectedDate, view);
  }
}, [view]);

// Load services once - UNCHANGED
useEffect(() => {
  if (!propServices) {
    fetch('/api/booking-v2/vendor/services', {
      headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
    })
    .then(r => r.json())
    .then(data => {
      console.log('🛍️ Services loaded:', data);
      setServices(data.services || []);
      
      // Set default service filter to empty (all services)
      setSelectedServiceFilter('');
    })
    .catch(console.error);
  }
}, [propServices]);

// Debug log to see what bookings we actually have - UPDATED
useEffect(() => {
  if (calendarData && (view === 'week' || view === 'month')) {
    console.log('📊 WEEK/MONTH VIEW DATA CHECK:', {
      view,
      totalBookings: calendarData.bookings?.length,
      startDate: calendarData.startDate,
      endDate: calendarData.endDate,
      dateRangeStart: dateRange.start.toISOString().split('T')[0],
      dateRangeEnd: dateRange.end.toISOString().split('T')[0],
      selectedServiceFilter: selectedServiceFilter || 'all',
      bookingsByDate: calendarData.bookings?.reduce((acc, b) => {
        acc[b.booking_date] = (acc[b.booking_date] || 0) + 1;
        return acc;
      }, {})
    });
  }
}, [calendarData, view, dateRange, selectedServiceFilter]);


// NEW: Helper function to get filtered bookings with date range
const getFilteredBookings = () => {
  if (!calendarData?.bookings) return [];
  
  let filtered = calendarData.bookings;
  
  // Filter by date range (always apply this)
  const startDateStr = dateRange.start.toISOString().split('T')[0];
  const endDateStr = dateRange.end.toISOString().split('T')[0];
  
  filtered = filtered.filter(booking => {
    const bookingDate = booking.booking_date;
    return bookingDate >= startDateStr && bookingDate <= endDateStr;
  });
  
  // Filter by service if selected
  if (selectedServiceFilter) {
    filtered = filtered.filter(
      booking => booking.service_id === parseInt(selectedServiceFilter)
    );
  }
  
  // Filter by status if selected
  if (statusFilter !== 'all') {
    filtered = filtered.filter(booking => booking.status === statusFilter);
  }
  
  console.log('🔍 Filtered bookings:', {
    total: calendarData.bookings.length,
    filtered: filtered.length,
    statusFilter,
    serviceFilter: selectedServiceFilter || 'all'
  });
  
  return filtered;
};

const handleBlockTimeSlot = async (formData, existingBlockId = null) => {
  try {
    const dateStr = selectedDate.toISOString().split('T')[0];
    
    const url = existingBlockId 
      ? `/api/booking-v2/vendor/calendar/blocked-slots/${existingBlockId}`
      : '/api/booking-v2/vendor/calendar/block-time';
    
    const method = existingBlockId ? 'PUT' : 'POST';
    
    const response = await fetch(url, {
      method,
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('token')}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        date: dateStr,
        startTime: formData.startTime,
        endTime: formData.endTime,
        reason: formData.reason,
        staffMember: formData.staffMember
      })
    });

    if (response.ok) {
      alert(existingBlockId ? 'Blocked time updated!' : 'Time slot blocked successfully!');
      setShowModal(null);
      // Auto-refresh calendar
      setForceRefresh(prev => prev + 1);
    } else {
      const errorData = await response.json();
      alert(errorData.error || 'Error processing request');
    }
  } catch (error) {
    console.error('Error:', error);
    alert('Error processing blocked time');
  }
};



const viewTypeToggle = (
  <div className="cm-view-type-toggle">
    <button
      onClick={() => {
        console.log('🔄 Changing view type to: timeline');
        setViewType('timeline');
      }}
      className={`cm-view-type-btn ${viewType === 'timeline' ? 'active' : ''}`}
    >
      Timeline
    </button>
    <button
      onClick={() => {
        console.log('🔄 Changing view type to: list');
        setViewType('list');
      }}
      className={`cm-view-type-btn ${viewType === 'list' ? 'active' : ''}`}
    >
      List
    </button>
  </div>
);

// Add service filter component
const serviceFilter = services.length > 0 ? (
  <div className="cm-service-filter">
    <label htmlFor="service-select">Service:</label>
    <select
      id="service-select"
      value={selectedServiceFilter}
      onChange={(e) => {
        console.log('🎯 Service filter changed to:', e.target.value || 'all');
        setSelectedServiceFilter(e.target.value);
      }}
      className="cm-select"
    >
      <option value="">All Services</option>
      {services.map(service => (
        <option key={service.id} value={service.id}>
          {service.service_name}
        </option>
      ))}
    </select>
  </div>
) : null;

// Add status filter component
const statusFilterComponent = (
  <div className="cm-service-filter">
    <label htmlFor="status-select">Status:</label>
    <select
      id="status-select"
      value={statusFilter}
      onChange={(e) => {
        console.log('📊 Status filter changed to:', e.target.value);
        setStatusFilter(e.target.value);
      }}
      className="cm-select"
    >
      <option value="all">All Statuses</option>
      <option value="pending">Pending</option>
      <option value="confirmed">Confirmed</option>
      <option value="cancelled">Cancelled</option>
    </select>
  </div>
);


  // NAVIGATION with forced refresh - Original working version
const navigateDate = (direction) => {
  const currentDate = selectedDate.toISOString().split('T')[0];
  
  const newDate = new Date(selectedDate);
  if (view === 'day') {
    newDate.setDate(selectedDate.getDate() + direction);
    // Update date range to match
    setDateRange({ start: newDate, end: newDate });
  } else if (view === 'week') {
    newDate.setDate(selectedDate.getDate() + (direction * 7));
    // Update date range for week
    const weekEnd = new Date(newDate);
    weekEnd.setDate(newDate.getDate() + 6);
    setDateRange({ start: newDate, end: weekEnd });
  } else if (view === 'month') {
    newDate.setMonth(selectedDate.getMonth() + direction);
    // Update date range for month
    const monthStart = new Date(newDate.getFullYear(), newDate.getMonth(), 1);
    const monthEnd = new Date(newDate.getFullYear(), newDate.getMonth() + 1, 0);
    setDateRange({ start: monthStart, end: monthEnd });
  }
  
  const newDateString = newDate.toISOString().split('T')[0];
  
  console.log('🏃 NAVIGATING:', currentDate, '→', newDateString);
  
  setSelectedDate(newDate);
  setForceRefresh(prev => prev + 1);
  
  setTimeout(() => {
    console.log('⚡ Direct call after navigation');
    fetchCalendarData(newDate, view);
  }, 100);
};


const goToToday = () => {
  const today = new Date();
  console.log('📍 GOING TO TODAY:', today.toISOString().split('T')[0]);
  
  // Set both selected date and date range to today
  setSelectedDate(today);
  setDateRange({ start: today, end: today });
  
  // Force day view when clicking "Today"
  setView('day');
  setForceRefresh(prev => prev + 1);
  
  // Fetch data for today
  fetchCalendarData(today, 'day');
};

// Add this function after goToToday
const navigateToDate = (targetDate) => {
  console.log('🎯 Navigating to specific date:', targetDate.toISOString().split('T')[0]);
  
  setSelectedDate(targetDate);
  setDateRange({ start: targetDate, end: targetDate });
  setView('day');
  setForceRefresh(prev => prev + 1);
  
  setTimeout(() => {
    fetchCalendarData(targetDate, 'day');
  }, 100);
};

// Make it globally accessible for modals
window.navigateCalendarToDate = navigateToDate;

// Make refresh accessible for modals
window.refreshCalendar = () => {
  setForceRefresh(prev => prev + 1);
};

// UPDATED: Manual refresh function
const manualRefresh = () => {
  console.log('🔄 MANUAL REFRESH');
  setForceRefresh(prev => prev + 1);
  fetchCalendarData(selectedDate, view);
};

  const generateWeekDays = () => {
  const days = [];
  const startOfWeek = new Date(selectedDate);
  startOfWeek.setDate(selectedDate.getDate() - selectedDate.getDay()); // Start from Sunday
  
  for (let i = 0; i < 7; i++) {
    const day = new Date(startOfWeek);
    day.setDate(startOfWeek.getDate() + i);
    days.push(day);
  }
  return days;
};

const generateMonthDays = () => {
  const days = [];
  const year = selectedDate.getFullYear();
  const month = selectedDate.getMonth();
  const firstDay = new Date(year, month, 1);
  const lastDay = new Date(year, month + 1, 0);
  
  // Add padding days from previous month
  const startPadding = firstDay.getDay();
  for (let i = startPadding - 1; i >= 0; i--) {
    const day = new Date(firstDay);
    day.setDate(day.getDate() - (i + 1));
    days.push({ date: day, isCurrentMonth: false });
  }
  
  // Add all days of current month
  for (let i = 1; i <= lastDay.getDate(); i++) {
    days.push({ date: new Date(year, month, i), isCurrentMonth: true });
  }
  
  // ADD THIS: Add padding days from next month to complete the grid
  const endPadding = 6 - lastDay.getDay(); // Days needed to reach Saturday
  for (let i = 1; i <= endPadding; i++) {
    const day = new Date(year, month + 1, i);
    days.push({ date: day, isCurrentMonth: false });
  }
  
  return days;
};

const getBookingsForDate = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const dateString = `${year}-${month}-${day}`;
  
  // Use filtered bookings instead of all bookings
  const filteredBookings = getFilteredBookings();
  
  const matchingBookings = filteredBookings.filter(b => {
    return b.booking_date === dateString;
  }) || [];
  
  if (matchingBookings.length > 0) {
    console.log(`🎯 Found ${matchingBookings.length} bookings for ${dateString}`);
  }
  
  return matchingBookings;
};

  // UTILITY FUNCTIONS
  const parseTime = (timeString) => {
    const [hours, minutes] = timeString.split(':').map(Number);
    return hours * 60 + minutes;
  };

  const formatTime = (minutes) => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hours.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}`;
  };

  const generateTimeSlots = () => {
    const slots = [];
    if (!calendarData?.businessHours) return slots;
    
    const businessHours = calendarData.businessHours.find(h => h.day_of_week === selectedDate.getDay());
    if (!businessHours || !businessHours.is_open) return slots;

    const openTime = parseTime(businessHours.open_time);
    const closeTime = parseTime(businessHours.close_time);
    
    for (let time = openTime; time < closeTime; time += 15) {
      slots.push({ time: formatTime(time), timeMinutes: time });
    }
    return slots;
  };

  const getSlotBookings = (timeSlot) => {
    if (!calendarData?.bookings) return [];
    
    return calendarData.bookings.filter(booking => {
      const bookingStart = parseTime(booking.booking_time);
      let bookingEnd;
      
      if (booking.end_time) {
        bookingEnd = parseTime(booking.end_time);
      } else if (booking.duration_minutes && parseInt(booking.duration_minutes) > 0) {
        bookingEnd = bookingStart + parseInt(booking.duration_minutes);
      } else {
        bookingEnd = bookingStart + 60;
      }
      
      return timeSlot >= bookingStart && timeSlot < bookingEnd;
    });
  };

  const isSlotBlocked = (timeSlot) => {
    if (!calendarData?.blockedSlots) return false;
    
    return calendarData.blockedSlots.some(blocked => {
      const blockStart = parseTime(blocked.start_time);
      const blockEnd = parseTime(blocked.end_time);
      return timeSlot >= blockStart && timeSlot < blockEnd;
    });
  };

  // BOOKING FUNCTIONS
const handleCreateManualBooking = async (bookingData) => {
  try {
    const response = await fetch('/api/booking-v2/vendor/manual-booking', {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('token')}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(bookingData)
    });

    if (response.ok) {
      alert('Manual booking created successfully!');
      setShowModal(null);
      // Auto-refresh without page reload
      setForceRefresh(prev => prev + 1);
    } else {
      const error = await response.json();
      alert(`Error: ${error.error}`);
    }
  } catch (error) {
    console.error('Error creating booking:', error);
    alert('Error creating booking');
  }
};

const blockTimeSlot = async (blockData) => {
  try {
    const response = await fetch('/api/booking-v2/vendor/block-time', {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('token')}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        date: selectedDate.toISOString().split('T')[0],
        ...blockData
      })
    });

    if (response.ok) {
      alert('Time slot blocked successfully!');
      setShowModal(null);
      // Auto-refresh without page reload
      setForceRefresh(prev => prev + 1);
    } else {
      const error = await response.json();
      alert(`Error: ${error.error}`);
    }
  } catch (error) {
    console.error('Error blocking time slot:', error);
    alert('Error blocking time slot');
  }
};

  const approveBooking = async (bookingId) => {
  try {
    const response = await fetch(`/api/booking-v2/vendor/bookings/${bookingId}/approve`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('token')}`,
        'Content-Type': 'application/json'
      }
    });

    if (response.ok) {
      console.log('✅ Booking approved:', bookingId);
      await fetchCalendarData(selectedDate, view);
      if (onDataChange) onDataChange();
      alert('Booking approved successfully! Customer will receive confirmation email.');
    } else {
      const error = await response.json();
      alert(`Error: ${error.error || 'Failed to approve booking'}`);
    }
  } catch (error) {
    console.error('Error approving booking:', error);
    alert('Error approving booking');
  }
};

const cancelBooking = async (bookingId) => {
  try {
    const response = await fetch(`/api/booking-v2/vendor/bookings/${bookingId}/cancel`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('token')}`,
        'Content-Type': 'application/json'
      }
    });

    if (response.ok) {
      console.log('❌ Booking cancelled:', bookingId);
      await fetchCalendarData(selectedDate, view);
      if (onDataChange) onDataChange();
      alert('Booking cancelled successfully!');
    } else {
      const error = await response.json();
      alert(`Error: ${error.error || 'Failed to cancel booking'}`);
    }
  } catch (error) {
    console.error('Error cancelling booking:', error);
    alert('Error cancelling booking');
  }
};



const handleSlotClick = (slot) => {
  // If clicking on blocked time
  if (slot.blocked) {
    setSelectedSlot(slot.blocked);
    setShowModal('editBlock');
    return;
  }

  // Check if slot is in the past
  const slotDateTime = new Date(`${selectedDate.toISOString().split('T')[0]}T${slot.time}`);
  const now = new Date();
  
  if (slotDateTime < now) {
    console.log('⏰ Cannot book past time:', slotDateTime);
    alert('Cannot create bookings for past dates and times');
    return;
  }

  // If clicking on existing booking
  if (slot.booking) {
    setSelectedCustomer(slot.booking);
    setShowModal('customer');
    return;
  }

  const bookings = getSlotBookings(slot.timeMinutes);
  const blocked = isSlotBlocked(slot.timeMinutes);
  
  if (bookings.length > 0) {
    setSelectedCustomer(bookings[0]);
    setShowModal('customer');
  } else if (blocked) {
    // Find the blocked slot data
    const blockedSlot = calendarData?.blockedSlots?.find(b => {
      const blockStart = parseTime(b.start_time);
      const blockEnd = parseTime(b.end_time);
      return slot.timeMinutes >= blockStart && slot.timeMinutes < blockEnd;
    });
    
    if (blockedSlot) {
      setSelectedSlot(blockedSlot);
      setShowModal('editBlock');
    } else {
      alert('This time slot is blocked');
    }
  } else {
    setSelectedSlot(slot);
    setShowModal('bookingDirect');
  }
};

const getFilteredStats = () => {
  const bookings = getFilteredBookings();
  
  const stats = {
    confirmed: bookings.filter(b => b.status === 'confirmed').length,
    pending: bookings.filter(b => b.status === 'pending').length,
    total: bookings.length,
    blocked: calendarData?.blockedSlots?.length || 0
  };
  
  console.log('📈 Calculated stats:', stats);
  
  return stats;
};

const stats = getFilteredStats();

  if (loading) {
    return (
      <div className="cm-loading-container">
        <div className="cm-spinner"></div>
        <span>Loading calendar...</span>
      </div>
    );
  }

  const timeSlots = generateTimeSlots();
  const todayBookings = calendarData?.bookings?.filter(b => 
    b.booking_date === selectedDate.toISOString().split('T')[0]
  ) || [];

  return (
    <div className="cm-container">
      <div className="cm-header">
        <div className="cm-header-content">
          <div className="cm-header-title">
            <Calendar className="cm-header-icon" />
            <div>
              <h1>Calendar Management</h1>
              <p className="cm-header-subtitle">Manage bookings and availability</p>
            </div>
          </div>
          <div className="cm-header-actions">
            <button onClick={() => setShowModal('booking')} className="cm-button cm-button-primary">
              <Plus className="cm-icon" />
              <span className="cm-button-text">New Booking</span>
            </button>
            <button onClick={() => setShowModal('block')} className="cm-button cm-button-secondary">
              <Clock className="cm-icon" />
              <span className="cm-button-text">Block Time</span>
            </button>
          </div>
        </div>
      </div>

<div className="cm-nav-card">
  <div className="cm-date-range-selector">
    <label>Date:</label>
    <input
      type="date"
      value={dateRange.start.toISOString().split('T')[0]}
      onChange={(e) => {
        const newStart = new Date(e.target.value);
        setDateRange({ ...dateRange, start: newStart });
        // Don't fetch immediately, wait for search button
      }}
      className="cm-input"
      max={dateRange.end.toISOString().split('T')[0]}
    />
    <span>to</span>
    <input
      type="date"
      value={dateRange.end.toISOString().split('T')[0]}
      onChange={(e) => {
        const newEnd = new Date(e.target.value);
        if (newEnd >= dateRange.start) {
          setDateRange({ ...dateRange, end: newEnd });
          // Don't fetch immediately, wait for search button
        } else {
          alert('End date cannot be before start date');
        }
      }}
      className="cm-input"
      min={dateRange.start.toISOString().split('T')[0]}
    />
    
    {/* ADD SEARCH BUTTON */}
    <button 
      onClick={() => {
        console.log('🔍 Search button clicked');
        const daysDiff = Math.ceil((dateRange.end - dateRange.start) / (1000 * 60 * 60 * 24));
        let viewMode = 'day';
        
        if (daysDiff === 0) {
          viewMode = 'day';
        } else if (daysDiff > 0 && daysDiff <= 7) {
          viewMode = 'week';
        } else if (daysDiff > 7) {
          viewMode = 'month';
        }
        
        setSelectedDate(dateRange.start);
        setView(viewMode);
        setForceRefresh(prev => prev + 1);
        fetchCalendarData(dateRange.start, viewMode);
      }}
      className="cm-button cm-button-primary cm-search-btn"
    >
      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="11" cy="11" r="8"></circle>
        <path d="m21 21-4.35-4.35"></path>
      </svg>
      Search
    </button>
  </div>

  <div className="cm-date-nav">
    <button onClick={() => navigateDate(-1)} className="cm-nav-btn">
      <ChevronLeft className="cm-icon" />
    </button>
    <div className="cm-current-date">
      <div className="cm-date-main">
        {view === 'day' && selectedDate.toLocaleDateString('en-US', { 
          weekday: 'short', 
          month: 'short', 
          day: 'numeric' 
        })}
        {view === 'week' && `Week of ${dateRange.start.toLocaleDateString('en-US', { 
          month: 'short', 
          day: 'numeric' 
        })}`}
        {view === 'month' && selectedDate.toLocaleDateString('en-US', { 
          month: 'long', 
          year: 'numeric' 
        })}
      </div>
      <div className="cm-date-year">
        {selectedDate.getFullYear()}
      </div>
    </div>
    <button onClick={() => navigateDate(1)} className="cm-nav-btn">
      <ChevronRight className="cm-icon" />
    </button>
  </div>

  <div className="cm-nav-actions">
    {viewTypeToggle}
    {serviceFilter}
     {statusFilterComponent} 
    <button onClick={goToToday} className="cm-today-btn">
      Today
    </button>
  </div>
</div>


<div className="cm-stats-grid">
  <div className="cm-stat-card">
    <div className="cm-stat-value confirmed">
      {getFilteredStats().confirmed}
    </div>
    <div className="cm-stat-label">Confirmed</div>
  </div>
  <div className="cm-stat-card">
    <div className="cm-stat-value pending">
      {getFilteredStats().pending}
    </div>
    <div className="cm-stat-label">Pending</div>
  </div>
  <div className="cm-stat-card">
    <div className="cm-stat-value total">
      {getFilteredStats().total}
    </div>
    <div className="cm-stat-label">Total</div>
  </div>
  <div className="cm-stat-card">
    <div className="cm-stat-value blocked">
      {getFilteredStats().blocked}
    </div>
    <div className="cm-stat-label">Blocked</div>
  </div>
</div>

<div className="cm-slots-card">
  <div className="cm-slots-header">
    <h3>
      {viewType === 'timeline' ? 'Timeline View' : 'Bookings List'}
      {selectedServiceFilter && (
        <span className="cm-filter-indicator">
          - {services.find(s => s.id === parseInt(selectedServiceFilter))?.service_name}
        </span>
      )}
    </h3>
    <span className="cm-slots-count">
      {getFilteredBookings().length} {viewType === 'timeline' ? 'bookings' : 'items'}
    </span>
  </div>
  
  {viewType === 'list' ? (
    <BookingListView
      bookings={getFilteredBookings()}
      onApprove={approveBooking}
      onCancel={cancelBooking}
      selectedService={selectedServiceFilter}
      isProcessing={loading}
    />
  ) : view === 'day' ? (
    timeSlots.length === 0 ? (
      <div className="cm-empty-state">
        <Clock className="cm-empty-icon" />
        <h3>Closed Today</h3>
        <p>No business hours configured for this day</p>
      </div>
    ) : (
      <TimelineView 
        timeSlots={timeSlots}
        bookings={getFilteredBookings().filter(b => 
          b.booking_date === selectedDate.toISOString().split('T')[0]
        )}
        blockedSlots={calendarData.blockedSlots || []}
        onSlotClick={handleSlotClick}
        parseTime={parseTime}
        formatTime={formatTime}
      />
    )
  ) : view === 'week' ? (
    <div className="cm-week-grid">
      {generateWeekDays().map((day, index) => {
        const bookings = getFilteredBookings().filter(b => {
          const bookingDate = new Date(b.booking_date).toDateString();
          return bookingDate === day.toDateString();
        });
        const isToday = day.toDateString() === new Date().toDateString();
        const isSelected = day.toDateString() === selectedDate.toDateString();
        
        return (
          <button
            key={index}
            onClick={() => {
              const newDate = day;
              setSelectedDate(newDate);
              setDateRange({ start: newDate, end: newDate });
              setView('day');
              fetchCalendarData(newDate, 'day');
            }}
            className={`cm-week-day ${isToday ? 'today' : ''} ${isSelected ? 'selected' : ''}`}
          >
            <div className="cm-week-day-header">
              <div className="cm-week-day-name">
                {day.toLocaleDateString('en-US', { weekday: 'short' })}
              </div>
              <div className="cm-week-day-date">
                {day.getDate()}
              </div>
            </div>
            <div className="cm-week-day-bookings">
              <div className="cm-week-booking-count">
                {bookings.length} {bookings.length === 1 ? 'booking' : 'bookings'}
              </div>
              {bookings.slice(0, 3).map((booking, idx) => (
                <div key={idx} className="cm-week-booking-item">
                  {booking.booking_time} - {booking.customer_name}
                </div>
              ))}
              {bookings.length > 3 && (
                <div className="cm-week-more">+{bookings.length - 3} more</div>
              )}
            </div>
          </button>
        );
      })}
    </div>
  ) : (
    <div className="cm-month-grid">
      <div className="cm-month-header">
        {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
          <div key={day} className="cm-month-header-day">{day}</div>
        ))}
      </div>
      <div className="cm-month-days">
        {generateMonthDays().map((item, index) => {
          const bookings = getFilteredBookings().filter(b => {
            const bookingDate = new Date(b.booking_date).toDateString();
            return bookingDate === item.date.toDateString();
          });
          const isToday = item.date.toDateString() === new Date().toDateString();
          const isSelected = item.date.toDateString() === selectedDate.toDateString();
          
          return (
            <button
              key={index}
              onClick={() => {
                const newDate = item.date;
                setSelectedDate(newDate);
                setDateRange({ start: newDate, end: newDate });
                setView('day');
                fetchCalendarData(newDate, 'day');
              }}
              className={`cm-month-day ${!item.isCurrentMonth ? 'other-month' : ''} ${isToday ? 'today' : ''} ${isSelected ? 'selected' : ''}`}
            >
              <div className="cm-month-day-number">{item.date.getDate()}</div>
              {bookings.length > 0 && (
                <div className="cm-month-day-indicator">
                  {bookings.length}
                </div>
              )}
            </button>
          );
        })}
      </div>
    </div>
  )}
</div>


      {showModal === 'booking' && (
        <ManualBookingModal
          selectedDate={selectedDate}
          services={services}
          onClose={() => setShowModal(null)}
          onSubmit={handleCreateManualBooking}
        />
      )}


      {showModal === 'bookingDirect' && selectedSlot && (
      <ManualBookingModal
        selectedDate={selectedDate}
        preSelectedTime={selectedSlot.time}
        services={services}
        onClose={() => { setShowModal(null); setSelectedSlot(null); }}
        onSubmit={handleCreateManualBooking}
      />
    )}

      {showModal === 'block' && (
        <BlockTimeModal
          selectedDate={selectedDate}
          onClose={() => setShowModal(null)}
          onSubmit={blockTimeSlot}
        />
      )}

      {showModal === 'editBlock' && selectedSlot && (
      <BlockTimeModal
        selectedDate={selectedDate}
        existingBlock={selectedSlot}
        onClose={() => { 
          setShowModal(null); 
          setSelectedSlot(null); 
        }}
        onSubmit={handleBlockTimeSlot}
      />
    )}


     {showModal === 'customer' && selectedCustomer && (
      <CustomerDetailsModal
        customer={selectedCustomer}
        services={services}
        onClose={() => {
          setShowModal(null);
          setSelectedCustomer(null);
        }}
        onNavigateToDate={navigateToDate}  
      />
    )}

      {showModal === 'slotActions' && selectedSlot && (
        <SlotActionsModal
          slot={selectedSlot}
          onClose={() => {
            setShowModal(null);
            setSelectedSlot(null);
          }}
          onBooking={() => setShowModal('booking')}
          onBlock={() => setShowModal('block')}
        />
      )}
    </div>
  );
};

const TimelineView = ({ timeSlots, bookings, blockedSlots, onSlotClick, parseTime, formatTime }) => {
  // Generate hours for header
  const startHour = Math.floor(parseTime(timeSlots[0]?.time) / 60);
  const endHour = Math.ceil(parseTime(timeSlots[timeSlots.length - 1]?.time) / 60);
  const hours = [];
  for (let h = startHour; h <= endHour; h++) {
    hours.push(h);
  }

  // Generate time rows (every hour)
  const timeRows = [];
  for (let slot of timeSlots) {
    const minutes = parseTime(slot.time);
    if (minutes % 60 === 0) {
      timeRows.push({
        time: slot.time,
        minutes: minutes
      });
    }
  }

  // Filter bookings to only show those within business hours
  const firstSlotMinutes = parseTime(timeSlots[0].time);
  const lastSlotMinutes = parseTime(timeSlots[timeSlots.length - 1].time);
  
  const visibleBookings = bookings.filter(booking => {
    const bookingStart = parseTime(booking.booking_time);
    const bookingEnd = booking.end_time 
      ? parseTime(booking.end_time)
      : bookingStart + (parseInt(booking.duration_minutes) || 60);
    
    // Show booking if it overlaps with business hours
    return bookingStart < lastSlotMinutes && bookingEnd > firstSlotMinutes;
  });

  // Detect overlapping bookings and assign columns
  const getBookingLayout = () => {
    const sortedBookings = [...visibleBookings].sort((a, b) => {
      return parseTime(a.booking_time) - parseTime(b.booking_time);
    });

    const columns = [];
    
    sortedBookings.forEach(booking => {
      const bookingStart = parseTime(booking.booking_time);
      const bookingEnd = booking.end_time 
        ? parseTime(booking.end_time)
        : bookingStart + (parseInt(booking.duration_minutes) || 60);

      // Find a column where this booking doesn't overlap
      let columnIndex = 0;
      let placed = false;

      while (!placed) {
        if (!columns[columnIndex]) {
          columns[columnIndex] = [];
        }

        const overlaps = columns[columnIndex].some(existingBooking => {
          const existingStart = parseTime(existingBooking.booking_time);
          const existingEnd = existingBooking.end_time 
            ? parseTime(existingBooking.end_time)
            : existingStart + (parseInt(existingBooking.duration_minutes) || 60);

          return bookingStart < existingEnd && bookingEnd > existingStart;
        });

        if (!overlaps) {
          columns[columnIndex].push(booking);
          placed = true;
        } else {
          columnIndex++;
        }
      }
    });

    // Create layout with column info
    const layout = [];
    const totalColumns = columns.length;

    columns.forEach((column, colIndex) => {
      column.forEach(booking => {
        layout.push({
          booking,
          column: colIndex,
          totalColumns
        });
      });
    });

    return layout;
  };

  const bookingLayout = getBookingLayout();

  // Calculate booking positions with column support
  const getBookingPosition = (booking, column, totalColumns) => {
    const startMinutes = parseTime(booking.booking_time);
    const endMinutes = booking.end_time 
      ? parseTime(booking.end_time)
      : startMinutes + (parseInt(booking.duration_minutes) || 60);
    
    const totalMinutes = lastSlotMinutes - firstSlotMinutes;
    
    const top = ((startMinutes - firstSlotMinutes) / totalMinutes) * 100;
    const height = ((endMinutes - startMinutes) / totalMinutes) * 100;
    
    // Calculate horizontal position based on column
    const columnWidth = 96 / totalColumns; // 96% to leave margins
    const left = 2 + (column * columnWidth); // 2% initial offset
    const width = columnWidth - 1; // -1% for spacing between columns

    return { 
      top: `${top}%`, 
      height: `${height}%`,
      left: `${left}%`,
      width: `${width}%`
    };
  };

  // Calculate blocked slot positions
  const getBlockedPosition = (blocked) => {
    const startMinutes = parseTime(blocked.start_time);
    const endMinutes = parseTime(blocked.end_time);
    
    const totalMinutes = lastSlotMinutes - firstSlotMinutes;
    
    const top = ((startMinutes - firstSlotMinutes) / totalMinutes) * 100;
    const height = ((endMinutes - startMinutes) / totalMinutes) * 100;
    
    return { top: `${top}%`, height: `${height}%` };
  };

  const handleTimelineClick = (e, rowMinutes) => {
    const isBookingClick = e.target.closest('.cm-timeline-booking');
    const isBlockedClick = e.target.closest('.cm-timeline-blocked');
    
    if (!isBookingClick && !isBlockedClick) {
      onSlotClick({ time: formatTime(rowMinutes), timeMinutes: rowMinutes });
    }
  };

  return (
    <div className="cm-timeline-container">
      {/* Hour headers */}
      <div className="cm-timeline-header">
        <div className="cm-timeline-hours">
          {hours.map(hour => (
            <div key={hour} className="cm-timeline-hour">
              {hour.toString().padStart(2, '0')}:00
            </div>
          ))}
        </div>
      </div>

      <div className="cm-timeline-body">
        {/* Time labels */}
        <div className="cm-timeline-times">
          {timeRows.map(row => (
            <div key={row.time} className="cm-timeline-time-slot">
              {row.time}
            </div>
          ))}
        </div>

        {/* Timeline grid */}
        <div className="cm-timeline-grid">
          {/* Hour markers */}
          <div className="cm-timeline-hour-markers">
            {hours.map(hour => (
              <div key={hour} className="cm-timeline-hour-marker" />
            ))}
          </div>

          {/* Time rows */}
          {timeRows.map(row => (
            <div 
              key={row.time} 
              className="cm-timeline-row"
              onClick={(e) => handleTimelineClick(e, row.minutes)}
            />
          ))}

          {/* Bookings with column layout */}
          {bookingLayout.map(({ booking, column, totalColumns }, index) => {
            const position = getBookingPosition(booking, column, totalColumns);
            return (
              <div
                key={`${booking.id}-${index}`}
                className="cm-timeline-booking"
                style={{
                  top: position.top,
                  height: position.height,
                  left: position.left,
                  width: position.width
                }}
                onClick={() => onSlotClick({ 
                  time: booking.booking_time, 
                  timeMinutes: parseTime(booking.booking_time),
                  booking: booking 
                })}
                title={`${booking.customer_name} - ${booking.booking_time} to ${booking.end_time || 'TBD'}`}
              >
                <div className="cm-timeline-booking-name">{booking.customer_name}</div>
                {booking.service_name && (
                  <div className="cm-timeline-booking-service">{booking.service_name}</div>
                )}
                <div className="cm-timeline-booking-time">
                  {booking.booking_time} - {booking.end_time || 'TBD'}
                </div>
              </div>
            );
          })}

          {/* Blocked slots */}
    {/* Blocked slots */}
{blockedSlots
  .filter(blocked => {
    // Filter blocked slots to only show within business hours
    const blockStart = parseTime(blocked.start_time);
    const blockEnd = parseTime(blocked.end_time);
    return blockStart < lastSlotMinutes && blockEnd > firstSlotMinutes;
  })
  .map((blocked, idx) => {
    const position = getBlockedPosition(blocked);
    return (
      <div
        key={idx}
        className="cm-timeline-blocked"
        style={{
          top: position.top,
          height: position.height,
          left: '2%',
          width: '96%',
          cursor: 'pointer' // ADD THIS
        }}
        onClick={() => onSlotClick({ 
          time: blocked.start_time, 
          timeMinutes: parseTime(blocked.start_time),
          blocked: blocked  // ADD THIS - pass the blocked slot data
        })}
        title="Click to edit or delete blocked time" // ADD THIS
      >
        <div className="cm-timeline-blocked-text">
          🚫 Blocked {blocked.reason ? `- ${blocked.reason}` : ''}
        </div>
      </div>
    );
  })}


        </div>
      </div>
    </div>
  );
};

const SlotActionsModal = ({ slot, onClose, onBooking, onBlock }) => {
  return (
    <div className="cm-modal-overlay" onClick={onClose}>
      <div className="cm-modal-content cm-modal-small" onClick={(e) => e.stopPropagation()}>
        <div className="cm-modal-header">
          <h3>Time Slot: {slot.time}</h3>
          <button onClick={onClose} className="cm-modal-close">
            <X className="cm-icon" />
          </button>
        </div>
        
        <div className="cm-modal-actions-grid">
          <button onClick={onBooking} className="cm-action-card">
            <Plus className="cm-action-icon primary" />
            <div className="cm-action-text">
              <div className="cm-action-title">Create Booking</div>
              <div className="cm-action-desc">Add a customer booking</div>
            </div>
          </button>
          
          <button onClick={onBlock} className="cm-action-card">
            <Clock className="cm-action-icon secondary" />
            <div className="cm-action-text">
              <div className="cm-action-title">Block Time</div>
              <div className="cm-action-desc">Mark as unavailable</div>
            </div>
          </button>
        </div>
      </div>
    </div>
  );
};

const ManualBookingModal = ({ selectedDate, preSelectedTime, services, onClose, onSubmit }) => {
const [formData, setFormData] = useState({
  customerData: { name: '', email: '', phone: '' },
  serviceId: '',
  startTime: preSelectedTime || '09:00', // Use preSelectedTime if provided
  notes: '',
  internalNotes: '',
  staffMember: ''
});

  const handleSubmit = () => {
    if (!formData.customerData.name || !formData.startTime) {
      alert('Please fill in required fields');
      return;
    }
    onSubmit({
      customerData: formData.customerData,
      serviceId: formData.serviceId || null,
      bookingDate: selectedDate.toISOString().split('T')[0],
      startTime: formData.startTime,
      notes: formData.notes,
      internalNotes: formData.internalNotes,
      staffMember: formData.staffMember || null
    });
  };

  return (
    <div className="cm-modal-overlay" onClick={onClose}>
      <div className="cm-modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="cm-modal-header">
          <h3>Create Manual Booking</h3>
          <button onClick={onClose} className="cm-modal-close">
            <X className="cm-icon" />
          </button>
        </div>
        
        <div className="cm-form">
          <div className="cm-form-grid">
            <div className="cm-form-group">
              <label className="cm-label">
                Customer Name <span className="cm-required">*</span>
              </label>
              <input
                type="text"
                value={formData.customerData.name}
                onChange={(e) => setFormData(prev => ({
                  ...prev,
                  customerData: { ...prev.customerData, name: e.target.value }
                }))}
                className="cm-input"
                placeholder="John Doe"
              />
            </div>

            <div className="cm-form-group">
              <label className="cm-label">Email</label>
              <input
                type="email"
                value={formData.customerData.email}
                onChange={(e) => setFormData(prev => ({
                  ...prev,
                  customerData: { ...prev.customerData, email: e.target.value }
                }))}
                className="cm-input"
                placeholder="john@example.com"
              />
            </div>

            <div className="cm-form-group">
              <label className="cm-label">Phone</label>
              <input
                type="tel"
                value={formData.customerData.phone}
                onChange={(e) => setFormData(prev => ({
                  ...prev,
                  customerData: { ...prev.customerData, phone: e.target.value }
                }))}
                className="cm-input"
                placeholder="+27 xx xxx xxxx"
              />
            </div>

            <div className="cm-form-group">
              <label className="cm-label">Service</label>
              <select
                value={formData.serviceId}
                onChange={(e) => setFormData(prev => ({ ...prev, serviceId: e.target.value }))}
                className="cm-input"
              >
                <option value="">Select service (optional)</option>
                {services.map(service => (
                  <option key={service.id} value={service.id}>
                    {service.service_name} ({service.duration_minutes}min) - R{service.price || 'Free'}
                  </option>
                ))}
              </select>
            </div>

            <div className="cm-form-group cm-full-width">
              <label className="cm-label">
                Start Time <span className="cm-required">*</span>
              </label>
              <input
                type="time"
                value={formData.startTime}
                onChange={(e) => setFormData(prev => ({ ...prev, startTime: e.target.value }))}
                className="cm-input"
              />
            </div>

            <div className="cm-form-group cm-full-width">
              <label className="cm-label">Customer Notes</label>
              <textarea
                value={formData.notes}
                onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                className="cm-input cm-textarea"
                rows="2"
                placeholder="Visible to customer"
              />
            </div>

            <div className="cm-form-group cm-full-width">
              <label className="cm-label">Internal Notes</label>
              <textarea
                value={formData.internalNotes}
                onChange={(e) => setFormData(prev => ({ ...prev, internalNotes: e.target.value }))}
                className="cm-input cm-textarea"
                rows="2"
                placeholder="Private vendor notes"
              />
            </div>
          </div>

          <div className="cm-form-actions">
            <button onClick={onClose} className="cm-button cm-button-secondary">
              Cancel
            </button>
            <button onClick={handleSubmit} className="cm-button cm-button-primary">
              Create Booking
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const BlockTimeModal = ({ selectedDate, onClose, onSubmit, existingBlock = null }) => {
  const [formData, setFormData] = useState({
    startTime: existingBlock?.start_time || '12:00',
    endTime: existingBlock?.end_time || '13:00',
    reason: existingBlock?.reason || '',
    staffMember: existingBlock?.staff_member || ''
  });

  const isEditing = !!existingBlock;

  const handleSubmit = () => {
    if (!formData.startTime || !formData.endTime) {
      alert('Please fill in required fields');
      return;
    }
    onSubmit(formData, existingBlock?.id);
  };

const handleDelete = async () => {
  if (!window.confirm('Delete this blocked time slot?')) return;
  
  try {
    const response = await fetch(
      `/api/booking-v2/vendor/calendar/blocked-slots/${existingBlock.id}`,
      {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      }
    );

    if (response.ok) {
      alert('Blocked time deleted successfully!');
      onClose();
      // Trigger refresh using global function
      if (window.refreshCalendar) {
        window.refreshCalendar();
      }
    } else {
      alert('Error deleting blocked time');
    }
  } catch (error) {
    console.error('Error deleting blocked time:', error);
    alert('Error deleting blocked time');
  }
};

  return (
    <div className="cm-modal-overlay" onClick={onClose}>
      <div className="cm-modal-content cm-modal-small" onClick={(e) => e.stopPropagation()}>
        <div className="cm-modal-header">
          <h3>{isEditing ? 'Edit' : 'Block'} Time Slot</h3>
          <button onClick={onClose} className="cm-modal-close">
            <X className="cm-icon" />
          </button>
        </div>
        
        <div className="cm-form">
          <div className="cm-form-group">
            <label className="cm-label">
              Start Time <span className="cm-required">*</span>
            </label>
            <input
              type="time"
              value={formData.startTime}
              onChange={(e) => setFormData(prev => ({ ...prev, startTime: e.target.value }))}
              className="cm-input"
            />
          </div>

          <div className="cm-form-group">
            <label className="cm-label">
              End Time <span className="cm-required">*</span>
            </label>
            <input
              type="time"
              value={formData.endTime}
              onChange={(e) => setFormData(prev => ({ ...prev, endTime: e.target.value }))}
              className="cm-input"
            />
          </div>

          <div className="cm-form-group">
            <label className="cm-label">Reason</label>
            <input
              type="text"
              value={formData.reason}
              onChange={(e) => setFormData(prev => ({ ...prev, reason: e.target.value }))}
              className="cm-input"
              placeholder="Lunch break, maintenance, etc."
            />
          </div>

          <div className="cm-form-actions">
            {isEditing && (
              <button onClick={handleDelete} className="cm-button cm-button-danger">
                Delete
              </button>
            )}
            <button onClick={onClose} className="cm-button cm-button-secondary">
              Cancel
            </button>
            <button onClick={handleSubmit} className="cm-button cm-button-primary">
              {isEditing ? 'Update' : 'Block'} Time
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};



const CustomerDetailsModal = ({ customer, onClose, services, onNavigateToDate }) => {
  const [customerHistory, setCustomerHistory] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showReschedule, setShowReschedule] = useState(false);
  const [rescheduleDate, setRescheduleDate] = useState(customer.booking_date);
    const [loadingSlots, setLoadingSlots] = useState(false);
  const [availableSlots, setAvailableSlots] = useState([]);
  const [selectedSlot, setSelectedSlot] = useState(null);

  useEffect(() => {
    loadCustomerHistory();
  }, [customer]);

const loadCustomerHistory = async () => {
  // Skip loading history if no email
  if (!customer.customer_email || customer.customer_email.trim() === '') {
    console.log('📋 No email provided - skipping history');
    setLoading(false);
    return;
  }

  try {
    console.log('🔍 Loading history for:', customer.customer_email);
    
    const response = await fetch(
      `/api/booking-v2/vendor/customers/${encodeURIComponent(customer.customer_email)}/history`,
      { 
        headers: { 
          'Authorization': `Bearer ${localStorage.getItem('token')}` 
        }
      }
    );
    
    if (response.ok) {
      const data = await response.json();
      setCustomerHistory(data);
    } else {
      console.error('Failed to load history:', response.status);
    }
  } catch (error) {
    console.error('Error loading customer history:', error);
  } finally {
    setLoading(false);
  }
};

  const handleApprove = async () => {
    if (!window.confirm('Approve this booking?')) return;
    
    try {
      const response = await fetch(
        `/api/booking-v2/vendor/bookings/${customer.id}/approve`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (response.ok) {
        alert('Booking approved successfully! Customer will receive confirmation email.');
        onClose();
        // Trigger parent refresh
        if (window.refreshCalendar) window.refreshCalendar();
      } else {
        const errorData = await response.json();
        alert(errorData.error || 'Error approving booking');
      }
    } catch (error) {
      console.error('Error approving booking:', error);
      alert('Error approving booking');
    }
  };

const handleCancel = async () => {
  if (!window.confirm('Cancel this booking? This cannot be undone.')) return;
  
  try {
    // Detect booking type
    const bookingType = customer.booking_type || 'manual';
    
    // Different endpoints for different booking types
    const endpoint = bookingType === 'manual'
      ? `/api/booking-v2/vendor/calendar/manual-bookings/${customer.id}/cancel`
      : `/api/booking-v2/vendor/calendar/bookings/${customer.id}/cancel`;
    
    const response = await fetch(endpoint, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('token')}`,
        'Content-Type': 'application/json'
      }
    });

    if (response.ok) {
      alert('Booking cancelled successfully!');
      onClose();
      if (window.refreshCalendar) window.refreshCalendar();
    } else {
      const errorData = await response.json();
      alert(errorData.error || 'Error cancelling booking');
    }
  } catch (error) {
    console.error('Error cancelling booking:', error);
    alert('Error cancelling booking');
  }
};

  const fetchAvailableSlotsForReschedule = async (date, serviceId) => {
    setLoadingSlots(true);
    try {
      const url = `/api/booking-v2/vendor/availability?date=${date}${serviceId ? `&serviceId=${serviceId}` : ''}`;
      const response = await fetch(url, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      if (response.ok) {
        const data = await response.json();
        setAvailableSlots(data.availableSlots || []);
      } else {
        setAvailableSlots([]);
      }
    } catch (error) {
      console.error('Error fetching available slots:', error);
      setAvailableSlots([]);
    } finally {
      setLoadingSlots(false);
    }
  };

  const handleConfirmReschedule = async () => {
    if (!selectedSlot) {
      alert('Please select a time slot');
      return;
    }
    try {
      const bookingType = customer.booking_type || 'manual';
      const endpoint = bookingType === 'manual' 
        ? `/api/booking-v2/vendor/calendar/manual-bookings/${customer.id}`
        : `/api/booking-v2/vendor/calendar/bookings/${customer.id}`;
      const updateData = bookingType === 'manual' 
        ? {
            customerName: customer.customer_name,
            customerEmail: customer.customer_email,
            customerPhone: customer.customer_phone,
            serviceId: customer.service_id,
            bookingDate: rescheduleDate,
            startTime: selectedSlot.startTime,
            endTime: selectedSlot.endTime,
            notes: customer.notes || customer.special_requests,
            internalNotes: customer.internal_notes,
            staffMember: customer.staff_member,
            status: customer.status
          }
        : {
            booking_date: rescheduleDate,
            booking_time: selectedSlot.startTime,
            end_time: selectedSlot.endTime,
            service_id: customer.service_id,
            status: customer.status
          };
      const response = await fetch(endpoint, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(updateData)
      });
      if (response.ok) {
        alert(`Booking rescheduled successfully to ${rescheduleDate} at ${selectedSlot.startTime}!`);
        setShowReschedule(false);
        onClose();
        if (window.refreshCalendar) window.refreshCalendar();
        if (onNavigateToDate) {
          setTimeout(() => onNavigateToDate(new Date(rescheduleDate)), 300);
        }
      } else {
        const errorData = await response.json();
        alert(errorData.error || 'Error rescheduling booking');
      }
    } catch (error) {
      console.error('Error rescheduling booking:', error);
      alert('Error rescheduling booking');
    }
  };

  const handleRescheduleClick = () => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    const tomorrowStr = tomorrow.toISOString().split('T')[0];
    setRescheduleDate(tomorrowStr);
    setShowReschedule(true);
    setSelectedSlot(null);
    fetchAvailableSlotsForReschedule(tomorrowStr, customer.service_id);
  };


  const handleJumpToDate = () => {
    // Close modal
    onClose();
    
    // Navigate to the selected date
    if (onNavigateToDate) {
      onNavigateToDate(new Date(rescheduleDate));
    }
    
    // Show instruction message
    setTimeout(() => {
      alert(`Calendar moved to ${rescheduleDate}. Click on an available time slot to reschedule ${customer.customer_name}'s booking.`);
    }, 500);
  };

  const canApprove = customer.status === 'pending';
  const canCancel = customer.status !== 'cancelled';
  const canReschedule = customer.status !== 'cancelled';

if (showReschedule) {
  return (
    <div className="cm-modal-overlay" onClick={onClose}>
      <div className="cm-modal-content cm-modal-medium" onClick={(e) => e.stopPropagation()}>
        <div className="cm-modal-header">
          <h3>Reschedule Booking</h3>
          <button onClick={() => setShowReschedule(false)} className="cm-modal-close">
            <X className="cm-icon" />
          </button>
        </div>

        <div className="cm-reschedule-content">
          <div className="cm-info-card cm-current-booking">
            <h4>Current Booking</h4>
            <div className="cm-booking-summary">
              <div className="cm-summary-item"><strong>Customer:</strong> {customer.customer_name}</div>
              <div className="cm-summary-item"><strong>Date:</strong> {customer.booking_date}</div>
              <div className="cm-summary-item"><strong>Time:</strong> {customer.booking_time} - {customer.end_time || 'TBD'}</div>
              {customer.service_name && (
                <div className="cm-summary-item"><strong>Service:</strong> {customer.service_name}</div>
              )}
            </div>
          </div>

          <div className="cm-form-group">
            <label className="cm-label">
              <Calendar className="cm-icon-inline" />
              Select New Date
            </label>
            <input
              type="date"
              value={rescheduleDate}
              onChange={(e) => {
                setRescheduleDate(e.target.value);
                setSelectedSlot(null);
                if (e.target.value) {
                  fetchAvailableSlotsForReschedule(e.target.value, customer.service_id);
                }
              }}
              min={new Date().toISOString().split('T')[0]}
              className="cm-input"
            />
          </div>

          <div className="cm-time-slot-picker">
            <h4>
              <Clock className="cm-icon-inline" />
              Available Time Slots
            </h4>
            {loadingSlots ? (
              <div className="cm-loading-container">
                <div className="cm-spinner"></div>
                <span>Loading available slots...</span>
              </div>
            ) : availableSlots.length > 0 ? (
              <div className="cm-slot-grid">
                {availableSlots.map((slot, idx) => (
                  <button
                    key={idx}
                    className={`cm-slot-button ${selectedSlot?.startTime === slot.startTime ? 'selected' : ''}`}
                    onClick={() => setSelectedSlot(slot)}
                  >
                    <div className="cm-slot-time">{slot.startTime}</div>
                    {slot.duration && <div className="cm-slot-duration">{slot.duration}min</div>}
                    {slot.price && <div className="cm-slot-price">R{slot.price}</div>}
                    {slot.capacity && (
                      <div className="cm-slot-capacity">{slot.capacity.remaining}/{slot.capacity.max} available</div>
                    )}
                  </button>
                ))}
              </div>
            ) : rescheduleDate ? (
              <div className="cm-no-slots">
                <p>😔 No available slots for this date</p>
                <p>Please select another date</p>
              </div>
            ) : (
              <div className="cm-no-slots">
                <p>📅 Please select a date first</p>
              </div>
            )}
          </div>

          {selectedSlot && (
            <div className="cm-info-card cm-selected-slot">
              <h4>✓ New Booking Details</h4>
              <div className="cm-booking-summary">
                <div className="cm-summary-item"><strong>Date:</strong> {rescheduleDate}</div>
                <div className="cm-summary-item"><strong>Time:</strong> {selectedSlot.startTime} - {selectedSlot.endTime}</div>
                <div className="cm-summary-item"><strong>Duration:</strong> {selectedSlot.duration || 60} minutes</div>
                {selectedSlot.price && (
                  <div className="cm-summary-item"><strong>Price:</strong> R{selectedSlot.price}</div>
                )}
              </div>
            </div>
          )}
        </div>

        <div className="cm-form-actions">
          <button onClick={() => setShowReschedule(false)} className="cm-button cm-button-secondary">
            Cancel
          </button>
          <button onClick={handleConfirmReschedule} className="cm-button cm-button-primary" disabled={!selectedSlot}>
            {selectedSlot ? '✓ Confirm Reschedule' : 'Select a Time Slot'}
          </button>
        </div>
      </div>
    </div>
  );
}

  return (
    <div className="cm-modal-overlay" onClick={onClose}>
      <div className="cm-modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="cm-modal-header">
          <h3>Booking Details</h3>
          <button onClick={onClose} className="cm-modal-close">
            <X className="cm-icon" />
          </button>
        </div>

        {loading ? (
          <div className="cm-loading-container">
            <div className="cm-spinner"></div>
            <span>Loading customer history...</span>
          </div>
        ) : (
          <div className="cm-customer-details">
            <div className="cm-info-card">
              <div className="cm-info-header">
                <User className="cm-info-icon" />
                <h4>{customer.customer_name}</h4>
              </div>
<div className="cm-info-grid">
  <div className="cm-info-item">
    <Mail className="cm-icon-small" />
    <span>{customer.customer_email || 'No email provided'}</span>
  </div>
  <div className="cm-info-item">
    <Phone className="cm-icon-small" />
    <span>{customer.customer_phone || 'No phone'}</span>
  </div>
</div>

{/* ADD THIS: Show info message when no email */}
{!customer.customer_email && (
  <div style={{ 
    marginTop: '12px', 
    padding: '8px 12px', 
    background: '#fef3c7', 
    borderRadius: '6px',
    fontSize: '13px',
    color: '#92400e'
  }}>
    ℹ️ Manual booking - Customer history not available without email
  </div>
)}
            </div>

            <div className="cm-info-card">
              <h4>Current Booking</h4>
              <div className="cm-booking-detail">
                <strong>Date:</strong> {customer.booking_date}
              </div>
              <div className="cm-booking-detail">
                <strong>Time:</strong> {customer.booking_time} - {customer.end_time || 'TBD'}
              </div>
              <div className="cm-booking-detail">
                <strong>Service:</strong> {customer.service_name || 'General booking'}
              </div>
              <div className="cm-booking-detail">
                <strong>Status:</strong>
                <span className={`cm-status-badge ${customer.status}`}>
                  {customer.status}
                </span>
              </div>
              {customer.special_requests && (
                <div className="cm-booking-detail">
                  <strong>Requests:</strong> {customer.special_requests}
                </div>
              )}
              {customer.internal_notes && (
                <div className="cm-booking-detail">
                  <strong>Internal Notes:</strong> {customer.internal_notes}
                </div>
              )}
            </div>

            {/* Action Buttons */}
            <div className="cm-booking-actions">
              {canApprove && (
                <button 
                  onClick={handleApprove}
                  className="cm-button cm-button-success"
                >
                  ✓ Approve
                </button>
              )}
              {canReschedule && (
                <button 
                  onClick={handleRescheduleClick}
                  className="cm-button cm-button-warning"
                >
                  📅 Reschedule
                </button>
              )}
              {canCancel && (
                <button 
                  onClick={handleCancel}
                  className="cm-button cm-button-danger"
                >
                  ✗ Cancel
                </button>
              )}
            </div>

          {customerHistory && customerHistory.statistics && (
              <div className="cm-customer-stats">
                <div className="cm-stat-item">
                  <div className="cm-stat-num">{customerHistory.statistics.totalBookings}</div>
                  <div className="cm-stat-text">Total Bookings</div>
                </div>
                <div className="cm-stat-item">
                  <div className="cm-stat-num">{customerHistory.statistics.completedBookings}</div>
                  <div className="cm-stat-text">Completed</div>
                </div>
                <div className="cm-stat-item">
                  <div className="cm-stat-num">R{customerHistory.statistics.totalSpent}</div>
                  <div className="cm-stat-text">Total Spent</div>
                </div>
              </div>
            )}

            {customerHistory && customerHistory.bookings && customerHistory.bookings.length > 1 && (
              <div className="cm-info-card">
                <h4>Recent Bookings</h4>
                <div className="cm-recent-bookings">
                  {customerHistory.bookings.slice(1, 6).map((booking, index) => (
                    <div key={index} className="cm-recent-item">
                      <div className="cm-recent-info">
                        <div className="cm-recent-date">{booking.booking_date}</div>
                        <div className="cm-recent-service">{booking.service_name || 'General'}</div>
                      </div>
                      <span className={`cm-status-badge ${booking.status}`}>
                        {booking.status}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default CalendarManagement;